import { useState, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../../../hooks/useClickOutside';
import {
  useDateRangePicker,
  DATE_RANGE_PRESETS,
  DateRangePreset,
} from '../../../hooks/useDateRangePicker';
import {
  encodeCustomRange,
  formatDateRangeLabel,
  formatDateToString,
} from '../../../shared/utils/dateRangeUtils';
import { CalendarGrid } from './CalendarGrid';

interface DateRangePickerProps {
  value: string;
  onChange: (value: string) => void;
}

const MONTH_KEYS = [
  'dashboard.filters.months.january',
  'dashboard.filters.months.february',
  'dashboard.filters.months.march',
  'dashboard.filters.months.april',
  'dashboard.filters.months.may',
  'dashboard.filters.months.june',
  'dashboard.filters.months.july',
  'dashboard.filters.months.august',
  'dashboard.filters.months.september',
  'dashboard.filters.months.october',
  'dashboard.filters.months.november',
  'dashboard.filters.months.december',
] as const;

/**
 * Date range picker component with presets and calendar
 */
export const DateRangePicker = memo(function DateRangePicker({
  value,
  onChange,
}: DateRangePickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    state,
    handlePresetClick,
    handleDayClick,
    navigateMonth,
    isSelected,
    isInRange,
    isRangeStart,
    isRangeEnd,
  } = useDateRangePicker(value);

  // Click outside handler
  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, closeDropdown, isOpen);

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handlePresetSelect = useCallback(
    (preset: DateRangePreset) => {
      handlePresetClick(preset);
    },
    [handlePresetClick],
  );

  const handleApply = useCallback(() => {
    // When the user picked specific calendar days, selectedPreset is
    // 'Custom Range' but the real information is in startDate/endDate.
    // Emit an encoded value so consumers can parse it back into dates.
    // Single click → endDate is still null; treat it as a one-day range
    // (otherwise we'd emit the literal 'Custom Range' and the API call
    // would drop the date filter, returning all-time data).
    if (state.selectedPreset === 'Custom Range' && state.startDate) {
      const endDate = state.endDate ?? state.startDate;
      onChange(
        encodeCustomRange(
          formatDateToString(state.startDate),
          formatDateToString(endDate),
        ),
      );
    } else {
      onChange(state.selectedPreset);
    }
    setIsOpen(false);
  }, [onChange, state.selectedPreset, state.startDate, state.endDate]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handlePrevMonth = useCallback(() => {
    navigateMonth(-1);
  }, [navigateMonth]);

  const handleNextMonth = useCallback(() => {
    navigateMonth(1);
  }, [navigateMonth]);

  return (
    <div className="date-picker-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className={`date-picker-btn ${isOpen ? 'open' : ''}`}
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('dashboard.filters.dateRange.selectAria')}
      >
        <span className="date-picker-icon" aria-hidden="true">
          <span className="material-symbols-outlined">calendar_today</span>
        </span>
        <span className="date-picker-btn-label">
          {formatDateRangeLabel(value) || t('dashboard.filters.dateRange.last30days')}
        </span>
      </button>

      {isOpen && (
        <div className="date-picker-dropdown" role="dialog" aria-label={t('dashboard.filters.dateRange.pickerAria')}>
          {/* Presets */}
          <div className="date-picker-presets">
            {DATE_RANGE_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={`date-preset-btn ${state.selectedPreset === preset.value ? 'selected' : ''}`}
                onClick={() => handlePresetSelect(preset)}
              >
                {t(preset.label)}
              </button>
            ))}
          </div>

          {/* Calendar */}
          <div>
            <div className="date-picker-calendar">
              <div className="calendar-header">
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={handlePrevMonth}
                  aria-label={t('dashboard.filters.dateRange.previousMonth')}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_left
                  </span>
                </button>
                <span className="calendar-title">
                  {t(MONTH_KEYS[state.currentMonth.getMonth()])} {state.currentMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={handleNextMonth}
                  aria-label={t('dashboard.filters.dateRange.nextMonth')}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_right
                  </span>
                </button>
              </div>

              <CalendarGrid
                currentMonth={state.currentMonth}
                isSelected={isSelected}
                isInRange={isInRange}
                isRangeStart={isRangeStart}
                isRangeEnd={isRangeEnd}
                onDayClick={handleDayClick}
              />
            </div>

            {/* Footer */}
            <div className="date-picker-footer">
              <button
                type="button"
                className="date-picker-cancel-btn"
                onClick={handleCancel}
              >
                {t('dashboard.filters.dateRange.cancel')}
              </button>
              <button
                type="button"
                className="date-picker-apply-btn"
                onClick={handleApply}
              >
                {t('dashboard.filters.dateRange.apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DateRangePicker;
